import { Dispatch } from "react";

import { PaymentProviders } from "@/constants/payment";
import { PaymentAction } from "@/utils/payment/reducer";
import {
    BRAINTREE_CHECKOUT_CONTAINER,
    getClientSettings,
    getCustomerId,
    PAYMENT_WRAPPER_SELECTOR,
    PRIMER_CHECKOUT_CONTAINER,
} from "@/utils/payment/settings";
import { Sentry } from "@/utils/trackers";
import { createPaymentClient } from "@palta-brain/web-sdk/dist/cjs/src/CreatePaymentClient";
import {
    PaymentClientI as LegacyPaymentClientI,
    PaymentFormSettings,
    PricePoint,
    PricePointRequest,
    showPaymentFormData,
} from "@palta-brain/web-sdk/dist/cjs/src/type";

enum PlatformCode {
    MobileWeb = "mobile_web",
    DesktopWeb = "desktop_web",
    Ios = "ios",
    Android = "android",
}

interface PaymentClientI extends LegacyPaymentClientI {
    getPricePoints: (request: PricePointRequest) => Promise<PricePoint[]>;
}
class PaymentClient {
    private _client: PaymentClientI | null = null;

    init(
        email: string,
        isApplePayAvailable: boolean | null,
        paywallName: string,
        dispatchPayment: Dispatch<PaymentAction>
    ) {
        if (!this._client) {
            try {
                const settings = getClientSettings(
                    email,
                    isApplePayAvailable,
                    paywallName,
                    dispatchPayment
                );
                this._client = createPaymentClient(settings) as PaymentClientI;
            } catch (e) {
                Sentry.captureException(e, {
                    isNewPaymentVersion: true,
                    email,
                });
            }
        }
    }

    async showPaymentForm(
        settings: PaymentFormSettings,
        primerOptions: showPaymentFormData["primerOption"],
        brainTreeOptions: showPaymentFormData["braintreeOption"]
    ): Promise<PaymentProviders | null> {
        if (this._client) {
            try {
                const data = await this._client.showPaymentForm({
                    settings,
                    primerOption: primerOptions,
                    braintreeOption: brainTreeOptions,
                    paymentWrapperSelector: PAYMENT_WRAPPER_SELECTOR,
                    primerCheckoutFormSelector: PRIMER_CHECKOUT_CONTAINER,
                    braintreeCheckoutFormSelector: BRAINTREE_CHECKOUT_CONTAINER,
                });

                return data?.getConfiguration
                    ? PaymentProviders.BrainTree
                    : PaymentProviders.Primer;
            } catch (e) {
                Sentry.captureException(e, {
                    isNewPaymentVersion: true,
                    country: settings.countryCode,
                    email: settings.email,
                    orderId: settings.orderId,
                });
            }
        }

        return null;
    }

    async updatePaymentForm(settings: PaymentFormSettings) {
        if (this._client) {
            try {
                await this._client.updatePaymentForm(settings);
            } catch (e) {
                Sentry.captureException(e, {
                    isNewPaymentVersion: true,
                    country: settings.countryCode,
                    email: settings.email,
                    orderId: settings.orderId,
                });
            }
        }
    }

    async getPricePoints(uuid: string, country: string, isMobile: boolean) {
        if (this._client) {
            return await this._client.getPricePoints({
                ident: [],
                customerId: getCustomerId(uuid),
                countryCode: country,
                platformCode: isMobile ? PlatformCode.MobileWeb : PlatformCode.DesktopWeb,
            });
        }
    }
}

export default new PaymentClient();
